/**
 * Team-Chat sidebar — 1:1 port of the prototype `isTeamchat` sidebar block
 * (Hausverwaltung Hero.dc.html L442–464): title + search, the "Team-Kanäle"
 * channel list and the "Direktnachrichten" DM rail with the active group thread.
 */
import { ld } from "@/lib/theme/light-dark";
import { Search, SquarePen, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";

import { H, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { DmRow } from "./module-teamchat-data";
import { channels, dmRowsBottom, dmRowsTop } from "./module-teamchat-data";

const MONO_HEADER: CSSProperties = {
  fontFamily: H.label,
  fontSize: 10,
  letterSpacing: "0.12em",
  color: H.inkGhost,
  textTransform: "uppercase",
  padding: "16px 9px 8px",
};

function ChannelRow(props: { readonly name: string }): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 9px",
        borderRadius: 8,
        fontSize: 13.5,
        color: ld("rgba(11,15,25,0.72)", "rgba(250,250,250,0.72)"),
      }}
    >
      <span style={{ color: H.inkGhost, fontWeight: 600 }}>#</span>
      {props.name}
    </div>
  );
}

function DmRowItem(props: { readonly row: DmRow }): ReactNode {
  const { row } = props;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "5px 9px",
        borderRadius: 8,
      }}
    >
      <span style={{ position: "relative", flexShrink: 0 }}>
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: row.avBg,
            color: row.avFg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {row.initials}
        </span>
        <span
          style={{
            position: "absolute",
            right: -1,
            bottom: -1,
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: row.statusDot,
            border: `2px solid ${ld("#FBFBFC", "#1d1f23")}`,
          }}
        />
      </span>
      <span style={{ fontSize: 13.5, color: H.inkSoft }}>{row.name}</span>
    </div>
  );
}

/** The active group-DM row (highlighted, "Carla Vogt, …" + count badge). */
function GroupDmRow(): ReactNode {
  const t = useTranslations("marketing.moduleTeamchatSidebar");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "5px 9px",
        borderRadius: 8,
        background: H.accentSoft,
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 8,
          background: ld("#fff", "#181b1f"),
          border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
          flexShrink: 0,
          position: "relative",
        }}
      >
        <Users width={14} height={14} strokeWidth={1.9} />
      </span>
      <span
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          color: H.accent,
          flex: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {t("carlaVogt")}
      </span>
      <span style={{ fontSize: 11, color: H.accent, fontWeight: 600 }}>2</span>
    </div>
  );
}

/** Full Team-Chat sidebar (220px, channel list + DM rail). */
export function TeamchatSidebar(): ReactNode {
  const tModuleTeamchatData = useTranslations("marketing.moduleTeamchatData");
  const t = useTranslations("marketing.moduleTeamchatSidebar");
  const tData = useTranslations("marketing.moduleTeamchatData");
  return (
    <div
      style={{
        width: SIDEBAR_WIDTH,
        borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#FBFBFC", "#1d1f23"),
        padding: "16px 12px",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 6px 12px",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700 }}>{t("teamchat")}</span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)"),
            cursor: "pointer",
          }}
        >
          <SquarePen width={16} height={16} strokeWidth={1.7} />
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 34,
          padding: "0 11px",
          background: ld("#F1F2F4", "#2b2e32"),
          borderRadius: 9,
          color: H.inkDim,
          fontSize: 13,
        }}
      >
        <Search width={15} height={15} strokeWidth={1.8} />
        {t("suchen")}
      </div>
      <div style={MONO_HEADER}>{t("teamKanaele")}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {channels(tData).map((name) => (
          <ChannelRow key={name} name={name} />
        ))}
      </div>
      <div style={MONO_HEADER}>{t("direktnachrichten")}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {dmRowsTop(tModuleTeamchatData).map((row) => (
          <DmRowItem key={row.name} row={row} />
        ))}
        <GroupDmRow />
        {dmRowsBottom(tModuleTeamchatData).map((row) => (
          <DmRowItem key={row.name} row={row} />
        ))}
      </div>
    </div>
  );
}
